<template>
  <div
    v-if="currentNotification"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @click="onBackdropClick"
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden"
      @click.stop
    >
      <div
        class="bg-blue-600 text-white px-6 py-4 flex items-center justify-between"
      >
        <h3 class="text-lg font-semibold flex items-center">
          <span class="mr-2">📩</span>
          {{ currentNotification.title }}
        </h3>
        <div class="flex items-center gap-3">
          <span
            v-if="notificationQueue.length > 1"
            class="text-sm bg-blue-700 px-2 py-1 rounded"
          >
            {{ notificationQueue.length }} messages
          </span>
          <button
            @click="closeNotification"
            class="text-white hover:text-gray-200 text-2xl leading-none"
          >
            &times;
          </button>
        </div>
      </div>
      <div class="px-6 py-4">
        <p class="text-gray-700 whitespace-pre-wrap">
          {{ currentNotification.body }}
        </p>
        <p class="text-gray-400 text-sm mt-4">
          {{ formatTimestamp(currentNotification.timestamp) }}
        </p>
      </div>
      <div class="px-6 py-4 bg-gray-50 flex justify-between">
        <div>
          <button
            v-if="notificationQueue.length > 1"
            @click="dismissAll"
            class="px-4 py-2 text-red-600 hover:text-red-800"
          >
            Dismiss All ({{ notificationQueue.length }})
          </button>
        </div>
        <div class="flex gap-3">
          <button
            @click="showLater"
            class="px-4 py-2 text-gray-500 hover:text-gray-700"
          >
            Later
          </button>
          <button
            @click="markAsViewed"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
          >
            {{ notificationQueue.length > 1 ? "Next" : "Mark as Viewed" }}
          </button>
          <button
            v-if="currentNotification.url && currentNotification.url !== '/'"
            @click="navigateToUrl"
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            View
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "NotificationModal",
  data() {
    return {
      notificationQueue: [],
      notificationsService: null
    };
  },
  computed: {
    currentNotification() {
      return this.notificationQueue.length > 0
        ? this.notificationQueue[0]
        : null;
    }
  },
  mounted() {
    this.initServiceWorkerListener();
    this.initFocusListener();
    this.loadNotificationsService();
  },
  beforeDestroy() {
    window.removeEventListener("focus", this.onWindowFocus);
  },
  methods: {
    async loadNotificationsService() {
      try {
        this.notificationsService = await import(
          "@/api/NotificationsService.js"
        );
        this.fetchPendingNotifications();
      } catch (e) {
        console.log("Failed to load notifications service:", e);
      }
    },
    initServiceWorkerListener() {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.addEventListener("message", event => {
          if (event.data && event.data.type === "SHOW_NOTIFICATION_MODAL") {
            this.addToQueue(event.data.data);
          }
        });
      }
    },
    initFocusListener() {
      window.addEventListener("focus", this.onWindowFocus);
    },
    onWindowFocus() {
      this.fetchPendingNotifications();
    },
    async fetchPendingNotifications() {
      if (!this.notificationsService) return;

      try {
        const response = await this.notificationsService.getPendingNotifications(
          "free_text"
        );
        if (response.status === "success" && response.notifications) {
          // Add notifications that aren't already in the queue
          response.notifications.forEach(notification => {
            const exists = this.notificationQueue.some(
              n => n.uuid === notification.uuid
            );
            if (!exists) {
              this.notificationQueue.push({
                uuid: notification.uuid,
                title: notification.title || "Notification",
                body: notification.body || "",
                url: notification.url,
                timestamp: notification.created_at
                  ? new Date(notification.created_at).getTime()
                  : null
              });
            }
          });
        }
      } catch (e) {
        console.log("Failed to fetch pending notifications:", e);
      }
    },
    addToQueue(data) {
      // Check if already in queue by UUID
      if (data.notificationUUID) {
        const exists = this.notificationQueue.some(
          n => n.uuid === data.notificationUUID
        );
        if (exists) return;
      }

      this.notificationQueue.push({
        uuid: data.notificationUUID || null,
        title: data.title || "Notification",
        body: data.body || "",
        url: data.url,
        timestamp: data.timestamp
      });
    },
    onBackdropClick() {
      // Click outside does nothing - user must choose an action
    },
    showLater() {
      // Just hide the current notification without marking as dismissed
      // It will show again next time the user visits or on page focus
      this.notificationQueue.shift();
    },
    async markAsViewed() {
      const notification = this.notificationQueue.shift();

      // Call API to mark as dismissed if it has a UUID
      if (notification && notification.uuid && this.notificationsService) {
        try {
          await this.notificationsService.dismissNotification(
            notification.uuid
          );
        } catch (e) {
          console.log("Failed to dismiss notification:", e);
        }
      }
    },
    async closeNotification() {
      // Alias for markAsViewed for backward compatibility
      await this.markAsViewed();
    },
    async dismissAll() {
      const uuids = this.notificationQueue.filter(n => n.uuid).map(n => n.uuid);

      this.notificationQueue = [];

      // Call API to dismiss all
      if (uuids.length > 0 && this.notificationsService) {
        try {
          await this.notificationsService.dismissAllNotifications(uuids);
        } catch (e) {
          console.log("Failed to dismiss notifications:", e);
        }
      }
    },
    async navigateToUrl() {
      if (this.currentNotification?.url) {
        this.$router.push(this.currentNotification.url);
      }
      await this.closeNotification();
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "";
      return new Date(timestamp).toLocaleString();
    }
  }
};
</script>
